import getFullAddress from '@utils/getFullAddress';
import PlacingOrderAddressFieldsCN from '../PlacingOrderAddressFields/PlacingOrderAddressFieldsCN';
import PlacingOrderAddressFieldsFirst
  from '../PlacingOrderAddressFields/PlacingOrderAddressFieldsFirst';
import PlacingOrderAddressFieldsHK from '../PlacingOrderAddressFields/PlacingOrderAddressFieldsHK';
import PlacingOrderAddressFieldsSecond
  from '../PlacingOrderAddressFields/PlacingOrderAddressFieldsSecond';
import PlacingOrderAddressFieldsThird
  from '../PlacingOrderAddressFields/PlacingOrderAddressFieldsThird';
import Switch from '@components/new/Switch';
import TextAreaContainer from '@containers/TextAreaContainer';
import { AddressTemplate, TLabelSelectedItem } from '@api/apiClient';
import { FieldError, FieldPath, useForm } from 'react-hook-form';
import { getCurrentLanguage } from '@utils/lang';
import { IPlacingOrderFormProps } from '../../placing-types';
import TextInputContainer from '@containers/TextInputContainer';
import {
  TAddress,
  TRegion,
  TSuggestionTR,
  TSuggestionTRByIndex,
  useDistrictSuggestions,
  useAddressSuggestionsCn,
  useAddressSuggestionsCnCity,
  useAddressSuggestionsCnCityDistricts,
  useAddressSuggestionsTr,
  useAddressSuggestionsTrByIndex
} from '@api/apiClient/address';
import { Title1 } from "@components/new/Typo";
import {
  textValidatorCN,
  textValidatorCNWithLatin,
  textValidatorHK,
  textValidatorHKWithLatin,
  textValidatorRU
} from '@utils/textValidator';
import { TSetState } from '@src/types.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useProfile } from '@contexts/ProfileContext';
import { useRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import styles from "./PlacingOrderForm.module.scss";
import { emptyAddress } from "../../utils/emptyAddress";

const PlacingOrderForm: React.FC<IPlacingOrderFormProps> = (props: IPlacingOrderFormProps): React.ReactElement => {

  const {
    addressTemplates,
    contactMinLength,
    // contactHint,
    // contactPlaceholder,
    country,
    // currentStep,
    dataGetValidation,
    // description,
    handlerMutateGetValidation,
    handlerOnClickDeleteAddress,
    handlerUpdateForm,
    // isNew,
    // numberOfSteps,
    regions,
    rememberAddress,
    // shipmentDates,
    showComment,
    showEmail,
    // showOneCountryPhone,
    // title,
    values,
    // withDateButtons,
    // withDatePicker,
    withFullAddressInEnglish,
    formRef,
  } = props;

  const [fullListAddresses, setFullListAddresses] = useState<AddressTemplate[]>([]);
  const [fullListRegions, setFullListRegions] = useState<TRegion[]>([]);
  const [savedRegion, setSavedRegion] = useState<string | undefined>(undefined);
  const [isMunicipality, setIsMunicipality] = useState<boolean>(false);
  const [selectedSuggestionCn, setSelectedSuggestionCn] = useState<TAddress | undefined>();
  const [selectedSuggestionTr, setSelectedSuggestionTr] = useState<TSuggestionTR | undefined>();
  const [selectedSuggestionTrByIndex, setSelectedSuggestionTrByIndex] = useState<TSuggestionTRByIndex | undefined>();
  const [validateAddress, setValidateAddress] = useState<boolean>(false);
  const [cnAddressTitle, setCnAddressTitle] = useState<string>("");

  const handleSelectedSuggestionCn: TSetState<TAddress | undefined> = (data) => {
    if ((countryCode === 'CN') && (typeof data !== "function")) setCnAddressTitle(data?.title ?? "");
    setValidateAddress(true);
    setSelectedSuggestionCn(data);
  }

  const currentLanguage = getCurrentLanguage();
  const hydratedRef = useRef(false);
  const { account } = useProfile();
  const { t } = useTranslation();

  const emptyAddressWithUpdate = useMemo(() => ({
    ...emptyAddress,
    address: {
      ...emptyAddress.address,
      countryCode: country === 'unknown' ? account?.region : country
    },
    ...(showComment && ({
      comment: ''
    })),
    ...(withFullAddressInEnglish ? ({
      addressEnglish: {
        area: '',
        city: '',
        cityDistrict: '',
        country: '',
        countryCode: country === 'unknown' ? account?.region : country,
        flat: '',
        house: '',
        rawLine: '',
        region: '',
        settlement: '',
        street: '',
        zipCode: ''
      }
    }) : {})
  }), [currentLanguage, country, account?.region, withFullAddressInEnglish]);

  const {
    clearErrors,
    control,
    getFieldState,
    getValues,
    setError,
    setValue,
    watch,
    reset
  } = useForm<AddressTemplate>({
    defaultValues: emptyAddressWithUpdate,
    mode: 'onChange',
    shouldUnregister: false
  });

  const city = watch('address.city');
  const cityAndStreet = watch('address.cityAndStreet');
  const cityDistrict = watch('address.cityDistrict');
  const cityEnglish = watch('addressEnglish.city');
  const countryCode = watch('address.countryCode');
  const flat = watch('address.flat');
  const fullName = watch('fullName');
  const house = watch('address.house');
  const phone = watch('phone');
  const region = watch('address.region');
  const street = watch('address.street');
  const streetEnglish = watch('addressEnglish.street');
  const zipCode = watch('address.zipCode');

  useEffect(() => {
    const filteredAddressTemplates: AddressTemplate[] =
        addressTemplates
            .filter((item: AddressTemplate) => item.address.rawLine !== emptyAddressWithUpdate.address.rawLine)
            .filter((item: AddressTemplate) => item.address.countryCode === countryCode)
            .reverse();
    const updatedAddressTemplates = filteredAddressTemplates.map((item: AddressTemplate) => ({
      ...item,
      address: {
        ...item.address,
        region: (item.address.region ?? '').replace(/省/, '')
      }
    }));
    setFullListAddresses([emptyAddressWithUpdate, ...updatedAddressTemplates]);
  }, [addressTemplates, countryCode, emptyAddressWithUpdate]);

  useEffect(() => {
    const filteredRegions: TRegion[] = regions.filter((item: TRegion) => item.country === countryCode);
    setFullListRegions(filteredRegions);
  }, [countryCode, regions]);

  useEffect(() => {
    if (region && region.length) {
      const regionFound: TRegion | undefined = fullListRegions.find((item: TRegion) => item.nameCn === region);
      if (countryCode === 'CN') {
        const isMunicipalityNew = regionFound ? regionFound.isMunicipality : false;
        setIsMunicipality(isMunicipalityNew);
        if (isMunicipalityNew && city) {
          setValue('address.city', "");
        }
      }
    } else {
      setIsMunicipality(false)
    }
  }, [region, fullListRegions]);

  useEffect(() => {
    if (!hydratedRef.current) {
      const template: AddressTemplate = values ?? emptyAddressWithUpdate;
      reset(template);
      hydratedRef.current = true;
    }
  }, [emptyAddressWithUpdate, reset, values]);

  useEffect(() => {
    const subscription = watch((formValues) => {
      handlerUpdateForm(country, 'values', formValues as AddressTemplate);
    });
    return () => subscription.unsubscribe();
  }, [country, handlerUpdateForm, watch]);

  useEffect(() => {
    if (hydratedRef.current && fullListRegions.length) {
      if (countryCode === 'CN') {
        setValue('address.region', savedRegion);
      }
    }
  }, [countryCode, fullListRegions, setValue]);

  useEffect(() => {
    if (countryCode !== 'CN') {
      setValue('address.region', "");
    }
    if (countryCode === 'HK') {
      setValue('address.city', "");
    }
  }, [countryCode]);

  useEffect(() => {
    cityEnglish && setValue('addressEnglish.city', cityEnglish.replace(/[^A-Za-z0-9\s.,#\-/]/g, ''));
  }, [cityEnglish]);

  useEffect(() => {
    streetEnglish && setValue('addressEnglish.street', streetEnglish.replace(/[^A-Za-z0-9\s.,#\-/]/g, ''));
  }, [streetEnglish]);

  useEffect(() => {
    if (countryCode === 'RU') {
      city && setValue('address.city', textValidatorRU(city));
      flat && setValue('address.flat', textValidatorRU(flat));
      fullName && setValue('fullName', fullName.replace(/[^A-Za-zА-Яа-яЁё0-9\s.,#\-/]/g, ''));
      house && setValue('address.house', textValidatorRU(house));
      phone && setValue('phone', phone.startsWith('7') || phone.startsWith('+7') ? phone : '7' + phone);
      street && setValue('address.street', textValidatorRU(street));
      zipCode && setValue('address.zipCode', textValidatorRU(zipCode));
    }
    if (countryCode === 'CN') {
      city && setValue('address.city', textValidatorCN(city));
      cityAndStreet && setValue('address.cityAndStreet', textValidatorCN(cityAndStreet));
      cityDistrict && setValue('address.cityDistrict', textValidatorCN(cityDistrict));
      flat && setValue('address.flat', textValidatorCN(flat));
      house && setValue('address.house', textValidatorCNWithLatin(house));
      street && setValue('address.street', textValidatorCN(street));
    }
    if (countryCode === 'HK') {
      city && setValue('address.city', textValidatorHK(city));
      cityAndStreet && setValue('address.cityAndStreet', textValidatorHK(cityAndStreet));
      cityDistrict && setValue('address.cityDistrict', textValidatorHK(cityDistrict));
      flat && setValue('address.flat', textValidatorHK(flat));
      house && setValue('address.house', textValidatorHKWithLatin(house));
      street && setValue('address.street', textValidatorHK(street));
    }
  }, [city, cityAndStreet, cityDistrict, countryCode, flat, fullName, house, phone, street, zipCode]);

  useEffect(() => {
    if (selectedSuggestionCn && validateAddress) {
      handlerMutateGetValidation({
        address: countryCode === "HK"
          ? `${selectedSuggestionCn.address}`
          : `${selectedSuggestionCn.province}${selectedSuggestionCn.city}${selectedSuggestionCn.district}${selectedSuggestionCn.address}`,
      });
    }
  }, [selectedSuggestionCn]);

  useEffect(() => {
    if (dataGetValidation) {
      setValidateAddress(false);
      const { message, result } = dataGetValidation;
      if (message === 'Success' && result) {
        const {
          addressComponents: {
            city,
            district,
            street,
            streetNumber
          },
          adInfo: {
            adcode,
          },
        } = result;

        if (selectedSuggestionCn) {
          if ((countryCode !== 'CN') && (countryCode !== 'HK')) {
            setValue('address.cityAndStreet', `${city} ${street}`);
            setValue('address.city', city);
          };
          if (countryCode !== 'CN') setValue('address.cityDistrict', district);
          setValue('address.house', streetNumber ? `${streetNumber} ${cnAddressTitle}`.trim() : getValues('address.house'));
          setValue('address.street', street);
          setValue('address.zipCode', adcode);
          setCnAddressTitle("");
        } else {
          setValue('address.house', streetNumber || getValues('address.house'));
        }
      }
    }
  }, [dataGetValidation]);

  useEffect(() => {
    if ((countryCode !== "HK") && (!selectedSuggestionCn?.address) && cityAndStreet) {
      setValue('address.city', cityAndStreet);
      setValue('address.street', '');
      setValue('address.cityDistrict', '');
    }
  }, [city, cityAndStreet, selectedSuggestionCn, street]);

  useEffect(() => {
    if (selectedSuggestionTrByIndex) {
      setValue('address.city', selectedSuggestionTrByIndex.city ?? '');
      setValue('address.zipCode', selectedSuggestionTrByIndex.postalCode ?? '');
    }
  }, [selectedSuggestionTrByIndex]);

  useEffect(() => {
    if (selectedSuggestionTr) {
      setValue('address.city', selectedSuggestionTr.city ?? '');
      setValue('address.zipCode', selectedSuggestionTr.postalCode ?? '');
      setValue('address.flat', selectedSuggestionTr.apartment ?? '');
      setValue('address.house', selectedSuggestionTr.houseNumber ?? '');
      setValue('address.street', selectedSuggestionTr.street ?? '');
    }
  }, [selectedSuggestionTr]);

  const useAddressSuggestionsHKDistrict = useCallback((search: string) => {
    return useDistrictSuggestions("HK", search || "");
  }, [countryCode]);

  const useAddressSuggestionsHK = useCallback((search: string) => {
    let regionId: string = '';
    if (region && region.length) {
      const regionFound: TRegion | undefined = fullListRegions.find((item: TRegion) => item.nameCn === region);
      regionId = regionFound ? regionFound.id : '';
    }
    return useAddressSuggestionsCn(regionId, `${city}${cityDistrict}` ?? "", countryCode === 'HK' ? search : "");
  }, [region, cityDistrict, countryCode]);

  const useAddressSuggestionsCnForRegion = useCallback((search: string) => {
    let regionId: string = '';
    if (region && region.length) {
      const regionFound: TRegion | undefined = fullListRegions.find((item: TRegion) => item.nameCn === region);
      regionId = regionFound ? regionFound.id : '';
    } else {
      setIsMunicipality(false)
    }
    if (countryCode === 'CN') setSavedRegion(region);
    return useAddressSuggestionsCnCity(regionId, search);
  }, [region]);

  const useAddressSuggestionsForRegion = useCallback((search: string) => {
    let regionId: string = '';
    if (region && region.length) {
      const regionFound: TRegion | undefined = fullListRegions.find((item: TRegion) => item.nameCn === region);
      regionId = regionFound ? regionFound.id : '';
    }
    if (countryCode === 'CN') setSavedRegion(region);
    return useAddressSuggestionsCn(regionId, "", search);
  }, [region]);

  const useAddressSuggestionsCnForCity = useCallback((search: string) => {
    let regionId: string = '';
    if (region && region.length) {
      const regionFound: TRegion | undefined = fullListRegions.find((item: TRegion) => item.nameCn === region);
      regionId = regionFound ? regionFound.id : '';
    }
    countryCode === 'CN' && setSavedRegion(region);
    return useAddressSuggestionsCnCityDistricts(regionId, (isMunicipality ? region : city) || "", search);
  }, [region, city]);

  const useAddressSuggestionsCnForCityDistrict = useCallback((search: string) => {
    let regionId: string = '';
    if (region && region.length) {
      const regionFound: TRegion | undefined = fullListRegions.find((item: TRegion) => item.nameCn === region);
      regionId = regionFound ? regionFound.id : '';
    }
    countryCode === 'CN' && setSavedRegion(region);
    return useAddressSuggestionsCn(regionId, cityDistrict || "", search);
  }, [region, city, cityDistrict]);

  const useAddressSuggestionsTrForRegionByIndex = useCallback((search: string) => {
    return useAddressSuggestionsTrByIndex(countryCode === 'TR' ? search : '');
  }, [zipCode]);

  const useAddressSuggestionsTrForRegion = useCallback((search: string) => {
    return useAddressSuggestionsTr({
      keyword: countryCode === 'TR' ? search : '',
      region: region ?? "",
      city: city ?? "",
    });
  }, [city, region]);

  const labelSelectedItem: TLabelSelectedItem = {
    address: getValues('address'),
    id: getValues('id')
  };

  const handlerGetFullAddress = (item: TLabelSelectedItem, from?: string): string => {
    let fullAddress: string = '';
    const land = t(`countries.${item.address.countryCode}`, { defaultValue: item.address.countryCode || '' });
    const address = { ...item.address, country: land };
    if (item.id === '') {
      if (from === 'renderItem') {
        fullAddress = `+ ${t('placingOrderModalWindow.form.address.newAddress')}`;
      } else {
        if (
            (countryCode === 'CN' || countryCode === 'HK') &&
            address.city === '' &&
            address.flat === '' &&
            address.house === '' &&
            address.region === '' &&
            address.zipCode === ''
        ) {
          fullAddress = `+ ${t('placingOrderModalWindow.form.address.newAddress')}`;
        } else if (
            (countryCode === 'TR' || countryCode === 'RU') &&
            address.city === '' &&
            address.flat === '' &&
            address.house === '' &&
            address.street === '' &&
            address.zipCode === ''
        ) {
          fullAddress = `+ ${t('placingOrderModalWindow.form.address.newAddress')}`;
        } else {
          fullAddress = getFullAddress(address);
        }
      }
    } else {
      fullAddress = getFullAddress(address);
    }
    return fullAddress;
  };

  const handlerSetCurrentAddress = (id: string): void => {
    const addressApplication: AddressTemplate = fullListAddresses.find((address: AddressTemplate) => address.id === id)!;
    setValidateAddress(false);
    if (countryCode === 'CN') {
      setValue('address', {
        ...addressApplication.address,
        ...(id === '' && { countryCode: countryCode }),
      });
    }
    if (countryCode === 'HK') {
      setValue('address', {
        ...addressApplication.address,
        // cityAndStreet: (`${addressApplication.address.city} ${addressApplication.address.street}`).trim(),
        ...(id === '' && { countryCode: countryCode }),
        region: fullListRegions[0].nameCn,
      });
    }
    if (countryCode === 'TR' || countryCode === 'RU') {
      setValue('address', addressApplication.address);
    }
    withFullAddressInEnglish && setValue('addressEnglish', addressApplication.addressEnglish);
    setValue('fullName', addressApplication.fullName);
    setValue('id', addressApplication.id);
    setValue('phone', addressApplication.phone);
    if (countryCode === 'RU') {
      setValue('email', addressApplication.email);
    }
    clearErrors();
    handlerUpdateForm(country, 'rememberAddress', id === '');
  };

  const onClickNext = (): boolean => {
    clearErrors();
    const formValues = [
      ...(countryCode === 'HK'
          ? [
            { key: 'address.street', value: getValues('address.street') },
            { key: 'address.cityDistrict', value: getValues('address.cityDistrict') }
          ]
          : []),
      ...(countryCode === 'CN'
          ? [
            { key: 'address.cityDistrict', value: getValues('address.cityDistrict') },
            { key: 'address.street', value: getValues('address.street') },
            { key: 'address.region', value: getValues('address.region') }
          ]
          : []),
      ...(((countryCode === 'CN') && !isMunicipality)
          ? [
            { key: 'address.city', value: getValues('address.city') },
          ]
          : []),
      ...(countryCode === 'TR' || countryCode === 'RU'
          ? [
            { key: 'address.city', value: getValues('address.city') },
            { key: 'address.street', value: getValues('address.street') }
          ]
          : []),
      { key: 'address.house', value: getValues('address.house') },
      ...(countryCode === 'RU'
          ? [
            { key: 'address.flat', value: getValues('address.flat') }
          ]
          : []),
      { key: 'address.zipCode', value: getValues('address.zipCode') },
      ...(withFullAddressInEnglish
          ? [
            { key: 'addressEnglish.city', value: getValues('addressEnglish.city') },
            { key: 'addressEnglish.street', value: getValues('addressEnglish.street') },
            { key: 'addressEnglish.house', value: getValues('addressEnglish.house') },
            { key: 'addressEnglish.flat', value: getValues('addressEnglish.flat') }
          ]
          : []),
      { key: 'fullName', value: getValues('fullName') },
      { key: 'phone', value: getValues('phone') }
    ];

    let areAllFieldsFilledIn: boolean = true;
    const contactsKey: FieldPath<AddressTemplate> = 'fullName';
    const phoneKey: FieldPath<AddressTemplate> = 'phone';
    const phoneError: FieldError | undefined = getFieldState(phoneKey).error;

    for (let i = 0; i < formValues.length; i++) {
      if (formValues[i].key === phoneKey) {
        if (formValues[i].value) {
          if (phoneError) areAllFieldsFilledIn = false;
        } else {
          areAllFieldsFilledIn = false;
        }
      } else if (formValues[i].key === contactsKey && contactMinLength) {
        if ((formValues[i].value?.trim().length ?? 0) < contactMinLength) {
          areAllFieldsFilledIn = false;
          setError(formValues[i].key as FieldPath<AddressTemplate>, {
            type: 'manual',
            message: t('placingOrderModalWindow.form.contacts.error')
          });
        }
      } else {
        if (!formValues[i].value) areAllFieldsFilledIn = false;
      }
    }
    if (areAllFieldsFilledIn) {
      const values: AddressTemplate = getValues();
      const land = t(`countries.${values.address.countryCode}`, { defaultValue: values.address.countryCode || '' });
      const updatedValues: AddressTemplate = {
        ...values,
        address: {
          ...values.address,
          country: land,
          rawLine: getFullAddress({ ...values.address, country: land }),
        },
        ...(showComment && {
          comment: values.comment,
        }),
        ...(withFullAddressInEnglish ? {
          addressEnglish: {
            ...values.addressEnglish,
            country: land,
            countryCode: countryCode,
            rawLine: getFullAddress({ ...values.addressEnglish, country: land }),
          }
        } : {})
      };
      // todo buttons[1].action();
      handlerUpdateForm(country, 'values', updatedValues);
    } else {
      for (let i = 0; i < formValues.length; i++) {
        if (!formValues[i].value) {
          setError(formValues[i].key as FieldPath<AddressTemplate>, {
            type: 'manual',
            message: t('defaultFormErrors.required')
          });
        }
      }
      handlerUpdateForm(country, 'rememberAddress', true);
    }
    return areAllFieldsFilledIn;
  };

  useImperativeHandle(formRef, () => ({
    submit: onClickNext,
  }));

  return (
    <div className={styles.placingForm}>
      {(countryCode === 'CN') && (
        <PlacingOrderAddressFieldsCN
          control={control}
          country={country}
          emptyAddressWithUpdate={emptyAddressWithUpdate}
          fullListAddresses={fullListAddresses}
          handlerGetFullAddress={handlerGetFullAddress}
          handlerOnClickDeleteAddress={handlerOnClickDeleteAddress}
          handlerSetCurrentAddress={handlerSetCurrentAddress}
          handlerSetSelectedSuggestion={handleSelectedSuggestionCn}
          labelSelectedItem={labelSelectedItem}
          region={region}
          regions={fullListRegions}
          isMunicipality={isMunicipality}
          useSuggestions={useAddressSuggestionsCnForRegion}
          useAddressSuggestionsForCity={useAddressSuggestionsCnForCity}
          useAddressSuggestionsForCityDistrict={useAddressSuggestionsCnForCityDistrict}
          withFullAddressInEnglish={withFullAddressInEnglish}
        />
      )}
      {(countryCode === 'unknown') && (
        <PlacingOrderAddressFieldsFirst
          control={control}
          country={country}
          emptyAddressWithUpdate={emptyAddressWithUpdate}
          fullListAddresses={fullListAddresses}
          handlerGetFullAddress={handlerGetFullAddress}
          handlerOnClickDeleteAddress={handlerOnClickDeleteAddress}
          handlerSetCurrentAddress={handlerSetCurrentAddress}
          handlerSetSelectedSuggestion={handleSelectedSuggestionCn}
          labelSelectedItem={labelSelectedItem}
          region={region}
          regions={fullListRegions}
          useSuggestions={useAddressSuggestionsForRegion}
          withFullAddressInEnglish={withFullAddressInEnglish}
          />
      )}
      {(countryCode === 'HK') && (
        <PlacingOrderAddressFieldsHK
          control={control}
          country={country}
          emptyAddressWithUpdate={emptyAddressWithUpdate}
          fullListAddresses={fullListAddresses}
          handlerGetFullAddress={handlerGetFullAddress}
          handlerOnClickDeleteAddress={handlerOnClickDeleteAddress}
          handlerSetCurrentAddress={handlerSetCurrentAddress}
          handlerSetSelectedSuggestion={handleSelectedSuggestionCn}
          labelSelectedItem={labelSelectedItem}
          region={region}
          cityDistrict={cityDistrict}
          useSuggestions={useAddressSuggestionsHK}
          useAddressSuggestionsHKDistrict={useAddressSuggestionsHKDistrict}
          withFullAddressInEnglish={withFullAddressInEnglish}
        />
      )}
      {countryCode === 'TR' && (
        <PlacingOrderAddressFieldsSecond
          city={city}
          control={control}
          emptyAddressWithUpdate={emptyAddressWithUpdate}
          fullListAddresses={fullListAddresses}
          handlerGetFullAddress={handlerGetFullAddress}
          handlerOnClickDeleteAddress={handlerOnClickDeleteAddress}
          handlerSetCurrentAddress={handlerSetCurrentAddress}
          handlerSetSelectedSuggestion={setSelectedSuggestionTr}
          handlerSetSelectedSuggestionByIndex={setSelectedSuggestionTrByIndex}
          labelSelectedItem={labelSelectedItem}
          street={street}
          useSuggestions={useAddressSuggestionsTrForRegion}
          useSuggestionsByIndex={useAddressSuggestionsTrForRegionByIndex}
          withFullAddressInEnglish={withFullAddressInEnglish}
          zipCode={zipCode}
        />
      )}
      {countryCode === 'RU' && (
        <PlacingOrderAddressFieldsThird
          control={control}
          country={country}
          emptyAddressWithUpdate={emptyAddressWithUpdate}
          fullListAddresses={fullListAddresses}
          handlerGetFullAddress={handlerGetFullAddress}
          handlerOnClickDeleteAddress={handlerOnClickDeleteAddress}
          handlerSetCurrentAddress={handlerSetCurrentAddress}
          labelSelectedItem={labelSelectedItem}
          withFullAddressInEnglish={withFullAddressInEnglish}
        />
      )}

      <div className={styles.addressSection}>
        <Title1 weight="semiBold">{t('placingOrderModalWindow.form.contacts.addressTitle')}</Title1>

        <div className={styles.addressRow}>
          <TextInputContainer
            control={control}
            name="fullName"
            title={t('placingOrderModalWindow.form.contacts.fullName')}
            type="text"
            rules={{ required: t("defaultFormErrors.required") }}
          />
          <TextInputContainer
            control={control}
            name="phone"
            // onlyCountry={country}
            title={t('placingOrderModalWindow.form.contacts.phone')}
            type="phone"
            rules={{ required: t("defaultFormErrors.required") }}
          />

          {showEmail ? (
            <TextInputContainer
              control={control}
              name="email"
              title="Email"
              type="email"
            />
          ) : null}
        </div>

        {showComment && (
          <TextAreaContainer
            control={control}
            maxLength={1000}
            name="comment"
            rows={3}
            title={t('placingOrderModalWindow.form.commentPickUp')}
          />
        )}

        <Switch
          checked={rememberAddress}
          onChange={() => handlerUpdateForm(country, 'rememberAddress', !rememberAddress)}
          title={t('placingOrderModalWindow.form.rememberData')}
        />
      </div>

    </div>
  );
};

export default PlacingOrderForm;
